/**
 @license
 Copyright (c) 2015-2026 Lablup Inc. All rights reserved.
 */
import { useThemeMode } from './useThemeMode';
import { useBAILogger } from 'backend.ai-ui';
import { useMemo } from 'react';
import { codeToHtml } from 'shiki';
import useSWR from 'swr';
import { Md5 } from 'ts-md5';

export const FALLBACK_LANG = 'txt';

export const useHighlight = (text: string, lang: string) => {
  const { logger } = useBAILogger();
  const { isDarkMode } = useThemeMode();
  const language = lang.toLowerCase();
  const matchedLanguage = languageMap[language] ? language : FALLBACK_LANG;
  const key = useMemo(() => Md5.hashStr(text), [text]);

  return useSWR(
    [matchedLanguage, isDarkMode ? 'd' : 'l', key].join('-'),
    async () => {
      try {
        return codeToHtml(text, {
          lang: matchedLanguage,
          theme: isDarkMode ? 'github-dark' : 'github-light',
        });
      } catch (error) {
        logger.error('shiki Highlight error:', error);
        return text;
      }
    },
  );
};

const languageMap: Record<string, boolean> = {
  abap: true,
  'actionscript-3': true,
  ada: true,
  apache: true,
  apex: true,
  apl: true,
  applescript: true,
  ara: true,
  asm: true,
  astro: true,
  awk: true,
  ballerina: true,
  bat: true,
  beancount: true,
  berry: true,
  bibtex: true,
  bicep: true,
  blade: true,
  c: true,
  cadence: true,
  clarity: true,
  clojure: true,
  cmake: true,
  cobol: true,
  codeql: true,
  coffee: true,
  cpp: true,
  crystal: true,
  csharp: true,
  css: true,
  cue: true,
  cypher: true,
  d: true,
  dart: true,
  dax: true,
  diff: true,
  docker: true,
  'dream-maker': true,
  elixir: true,
  elm: true,
  erb: true,
  erlang: true,
  fish: true,
  fsharp: true,
  gdresource: true,
  gdscript: true,
  gdshader: true,
  gherkin: true,
  'git-commit': true,
  'git-rebase': true,
  'glimmer-js': true,
  'glimmer-ts': true,
  glsl: true,
  gnuplot: true,
  go: true,
  graphql: true,
  groovy: true,
  hack: true,
  haml: true,
  handlebars: true,
  haskell: true,
  hcl: true,
  hjson: true,
  hlsl: true,
  html: true,
  http: true,
  imba: true,
  ini: true,
  java: true,
  javascript: true,
  'jinja-html': true,
  jison: true,
  json: true,
  json5: true,
  jsonc: true,
  jsonl: true,
  jsonnet: true,
  jssm: true,
  jsx: true,
  julia: true,
  kotlin: true,
  kusto: true,
  latex: true,
  less: true,
  liquid: true,
  lisp: true,
  logo: true,
  lua: true,
  make: true,
  markdown: true,
  marko: true,
  matlab: true,
  mdc: true,
  mdx: true,
  mermaid: true,
  mojo: true,
  narrat: true,
  nextflow: true,
  nginx: true,
  nim: true,
  nix: true,
  'objective-c': true,
  'objective-cpp': true,
  ocaml: true,
  pascal: true,
  perl: true,
  php: true,
  plsql: true,
  postcss: true,
  powerquery: true,
  powershell: true,
  prisma: true,
  prolog: true,
  proto: true,
  pug: true,
  puppet: true,
  purescript: true,
  python: true,
  r: true,
  raku: true,
  razor: true,
  reg: true,
  rel: true,
  riscv: true,
  rst: true,
  ruby: true,
  rust: true,
  sas: true,
  sass: true,
  scala: true,
  scheme: true,
  scss: true,
  shaderlab: true,
  shellscript: true,
  shellsession: true,
  smalltalk: true,
  solidity: true,
  sparql: true,
  splunk: true,
  sql: true,
  'ssh-config': true,
  stata: true,
  stylus: true,
  svelte: true,
  swift: true,
  'system-verilog': true,
  tasl: true,
  tcl: true,
  tex: true,
  toml: true,
  tsx: true,
  turtle: true,
  twig: true,
  typescript: true,
  v: true,
  vb: true,
  verilog: true,
  vhdl: true,
  viml: true,
  vue: true,
  'vue-html': true,
  vyper: true,
  wasm: true,
  wenyan: true,
  wgsl: true,
  wolfram: true,
  xml: true,
  xsl: true,
  yaml: true,
  zenscript: true,
  zig: true,
  bash: true,
  batch: true,
  be: true,
  'c#': true,
  cdc: true,
  clj: true,
  cmd: true,
  console: true,
  cql: true,
  cs: true,
  dockerfile: true,
  erl: true,
  'f#': true,
  fs: true,
  fsl: true,
  gjs: true,
  gts: true,
  hbs: true,
  hs: true,
  jade: true,
  js: true,
  kql: true,
  makefile: true,
  md: true,
  nar: true,
  nf: true,
  objc: true,
  perl6: true,
  properties: true,
  ps: true,
  ps1: true,
  py: true,
  ql: true,
  rb: true,
  rs: true,
  sh: true,
  shader: true,
  shell: true,
  spl: true,
  styl: true,
  ts: true,
  vim: true,
  vimscript: true,
  vy: true,
  yml: true,
  zsh: true,
  文言: true,
};
